<template>
  <div class="menu-container">
    <Menubar :model="items" class="bg-white navigation-menu">
      <template #start>
        <a href="/">
          <!-- Logo shown only on mobile when this component is directly used -->
          <img :src="logoPath" class="block md:hidden w-[250px]" alt="Insurance Express" />
        </a>
      </template>

      <template #item="{ item, props, hasSubmenu, root }">
        <a v-ripple class="flex items-center gap-2" v-bind="props.action" :class="{'router-link': item.type === 'router', 'external-link': item.type === 'external'}" :to="item.to" :href="item.to" @click="trackEvent('MainMenu', item.label, item.type)">
          <!-- Show special icon for 'Customer Service' with animation -->
          <template v-if="item.label === 'Customer Service'">
            <Icon v-if="!showIconifyIcon" :icon="item.iconName" class="text-red-500" width="20" height="20" />
            <Icon v-else icon="line-md:heart-filled" class="text-red-500 animate-pulse-slow" width="20" height="20" />
          </template>
          <!-- For all other menu items, use their iconName -->
          <template v-else>
            <Icon v-if="item.iconName" :icon="item.iconName" width="20" height="20" />
          </template>
          
          <!-- Item label with conditional styling -->
          <span :class="{ 'font-bold md:font-normal': item.label === 'Customer Service' }">{{ item.label }}</span>
          
          <!-- Badge if present -->
          <Badge v-if="item.badge" :class="{ 'ml-auto': !root, 'ml-2': root }" :value="item.badge" />
          
          <!-- Shortcut if present -->
          <span v-if="item.shortcut" class="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{{ item.shortcut }}</span>
          
          <!-- Dropdown chevron for items with submenu -->
          <Icon v-if="hasSubmenu" 
            :icon="root ? 'mdi:chevron-down' : 'mdi:chevron-right'" 
            class="text-primary"
            :class="{ 'ml-auto': !root }"
            width="16" 
            height="16" />
        </a>
      </template>
    </Menubar>
  </div>
</template>


<script>
import { Menubar, Badge } from '@/components/ui';
import { Icon } from '@iconify/vue';
import { mainMenuItems } from '@/data/navigation';
import logoPath from '@assets/images/brand/logos/insurance_express-main-logo.svg';

export default {
  name: 'AppHeaderDesktop',
  components: {
    Menubar,
    Badge,
    Icon,
  },
  data() {
    return {
      showIconifyIcon: false,
      items: mainMenuItems,
      logoPath
    };
  },
  mounted() {
    // After a delay, switch to the animated Iconify heart icon for Customer Service
    setTimeout(() => {
      this.showIconifyIcon = true;
    }, 2000);
  },
  methods: {
    trackEvent(category, label, type) {
      // Track menu clicks via analytics if needed
      if (window.gtag) {
        window.gtag('event', 'menu_click', {
          'event_category': category,
          'event_label': label,
          'link_type': type
        });
      }
    }
  }
};
</script>

<style scoped>
.menu-container {
  width: 100%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 100; /* Reduced from 1000 to prevent conflicts with Chatwoot */
}

/* Apply lower z-index to all Menubar elements - Updated for UIMenubar */
:deep(.ui-menubar-root), 
:deep(.ui-menubar-root-list),
:deep(.ui-menubar-submenu) {
  z-index: 110 !important; /* Reduced from 1100, still higher than menu container */
}

/* Style for submenus - Updated for UIMenubar */
:deep(.ui-menubar-submenu) {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0.5rem 0;
}

/* Style router links different from external links */
.router-link {
  color: #2c3e50;
}

.external-link {
  /* Styles for external links, if any needed (e.g., specific color) */
  /* Removed ::after pseudo-element styling for the icon */
}

/* Animation for pulse */
@keyframes pulse-slow {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 2s infinite;
}

/* Better styling for menu items - Updated for UIMenubar */
:deep(.ui-menuitem-link) {
  padding: 0.75rem 1rem !important;
  transition: background-color 0.2s, color 0.2s !important;
}

:deep(.ui-menuitem-link:hover) {
  background-color: #f8f9fa !important;
}

:deep(.ui-menubar-root-list > .ui-menuitem > .ui-menuitem-link) {
  font-weight: 500;
}

/* Responsive fixes for iPad and tablets (768px - 1024px) */
@media (min-width: 768px) and (max-width: 1024px) {
  .menu-container {
    overflow: visible;
  }
  
  :deep(.ui-menubar-content) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
  }
  
  :deep(.ui-menubar-root-list) {
    flex-wrap: nowrap;
    min-width: max-content;
    gap: 0.25rem;
  }
  
  :deep(.ui-menuitem-root) {
    flex-shrink: 0;
  }
  
  :deep(.ui-menuitem-link-root) {
    padding: 0.75rem 0.5rem !important;
    white-space: nowrap;
    font-size: 0.9rem;
  }
  
  :deep(.ui-menuitem-text) {
    font-size: 0.9rem;
  }
  
  :deep(.ui-menubar-submenu) {
    min-width: 200px;
    left: 0;
    right: auto;
  }
}

/* Medium desktop screens (1025px - 1280px) */
@media (min-width: 1025px) and (max-width: 1280px) {
  .menu-container {
    overflow: visible;
  }
  
  :deep(.ui-menubar-content) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: visible;
  }
  
  :deep(.ui-menubar-root-list) {
    flex-wrap: nowrap;
    min-width: max-content;
  }
  
  :deep(.ui-menuitem-root) {
    flex-shrink: 0;
  }
  
  :deep(.ui-menuitem-link-root) {
    padding: 0.75rem 0.75rem !important;
    white-space: nowrap;
  }
  
  :deep(.ui-menubar-submenu) {
    min-width: 200px;
    left: 0;
    right: auto;
  }
}

/* Prevent menu items from wrapping on desktop */
@media (min-width: 768px) {
  :deep(.ui-menubar-root-list) {
    flex-wrap: nowrap;
  }
  
  :deep(.ui-menuitem-root) {
    flex-shrink: 0;
  }
}
</style> 